import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Link } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  PortalLifecycleStatus,
  type PortalAwardOutcomeDto,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../../../common/formatting/useDisplayDateWithTime";
import { buildDoffinNoticeUrl } from "../../../sourcing-event/doffinNoticeUrl";
import { isDesignContest } from "../../buyer/wizard/utils/designContest";
import { useOpenPortalRound } from "./useOpenPortalRound";

// Looked up dynamically by status, so langen can't see these statically:
// t("The bid deadline has passed") t("Bids are under evaluation")
// t("The deadline for entries has passed") t("The jury is assessing the entries")
// t("This procurement has been awarded") t("This procurement has been cancelled")
// t("This procurement has concluded")
const MESSAGE: Partial<Record<PortalLifecycleStatus, string>> = {
  [PortalLifecycleStatus.DeadlinePassed]: "The bid deadline has passed",
  [PortalLifecycleStatus.UnderEvaluation]: "Bids are under evaluation",
  [PortalLifecycleStatus.Awarded]: "This procurement has been awarded",
  [PortalLifecycleStatus.Cancelled]: "This procurement has been cancelled",
  [PortalLifecycleStatus.Concluded]: "This procurement has concluded",
};

const DESIGN_CONTEST_MESSAGE: Partial<Record<PortalLifecycleStatus, string>> = {
  [PortalLifecycleStatus.DeadlinePassed]: "The deadline for entries has passed",
  [PortalLifecycleStatus.UnderEvaluation]: "The jury is assessing the entries",
};

export const EventLifecycleBanner = ({
  event,
  ownAwardOutcome,
}: {
  event: PortalSourcingEventDetailDto;
  ownAwardOutcome?: PortalAwardOutcomeDto | null;
}) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const { round: openRound, exit } = useOpenPortalRound(event, ownAwardOutcome);
  const status = event.lifecycle.status;
  const message = (isDesignContest(event.procedureType) && DESIGN_CONTEST_MESSAGE[status]) || MESSAGE[status];
  if (status === PortalLifecycleStatus.Open || !message) return null;

  if (openRound) {
    return (
      <Alert status="info" variant="subtle" rounded="md">
        <AlertIcon />
        <AlertTitle>
          {t("Negotiation round {{round}} is open until {{date}}", {
            round: openRound.roundNumber,
            date: displayDateWithTime(openRound.deadline),
          })}
        </AlertTitle>
      </Alert>
    );
  }

  if (exit) {
    return (
      <Alert status="warning" variant="subtle" rounded="md">
        <AlertIcon />
        <AlertTitle>
          {exit.reason === "declined"
            ? t("You declined negotiation round {{round}}.", {
                round: exit.round.roundNumber,
              })
            : t("You did not answer negotiation round {{round}} by its deadline, so you are not part of the next round unless the buyer invites you again.", {
                round: exit.round.roundNumber,
              })}
        </AlertTitle>
      </Alert>
    );
  }

  const isCancelled = status === PortalLifecycleStatus.Cancelled;
  const chakraStatus = isCancelled ? "error" : status === PortalLifecycleStatus.DeadlinePassed ? "warning" : "info";

  return (
    <Alert status={chakraStatus} variant="subtle" rounded="md">
      <AlertIcon />
      <Box>
        <AlertTitle>{t(message)}</AlertTitle>
        {isCancelled && event.lifecycle.cancelledReason && (
          <AlertDescription>{event.lifecycle.cancelledReason}</AlertDescription>
        )}
        {isCancelled && event.lifecycle.resultNoticeNumber && (
          <AlertDescription display="block">
            <Link
              href={buildDoffinNoticeUrl(event.lifecycle.resultNoticeNumber)}
              isExternal
              rel="noopener noreferrer"
              color="smPrimary"
            >
              {t("See the cancellation on Doffin")}
            </Link>
          </AlertDescription>
        )}
      </Box>
    </Alert>
  );
};
